import { motion } from "framer-motion";
import { Quote } from "lucide-react";

const testimonials = [
  {
    quote:
      "Aparna has a rare ability to weave heritage and contemporary practice into a single language. The studio is a treasure.",
    who: "Private Collector",
    where: "Delhi NCR",
  },
  {
    quote:
      "Our leadership offsite in the studio was less workshop, more revelation \u2014 genuinely transformative for the team.",
    who: "Corporate Leader",
    where: "Hospitality Group",
  },
  {
    quote:
      "Every corner of the gallery has a story. Every artwork asks something of you.",
    who: "Visiting Curator",
    where: "Mumbai",
  },
];

const press = [
  "As featured in",
  "Selected Press",
  "Editorial Mentions",
];

export default function Testimonials() {
  return (
    <section
      id="testimonials"
      className="relative py-24 md:py-36 bg-sand overflow-hidden"
      data-testid="testimonials-section"
    >
      <span className="pointer-events-none select-none absolute top-6 right-0 font-serif italic text-[18vw] leading-none text-ink/[0.04] whitespace-nowrap">
        Words
      </span>

      <div className="max-w-screen-2xl mx-auto px-6 md:px-10 relative">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-16 md:mb-24">
          <div className="md:col-span-8">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="font-body text-[11px] tracking-[0.35em] uppercase text-ochre mb-5"
            >
              Press &amp; Testimonials
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1] text-ink tracking-tight"
            >
              In their <span className="italic text-ochre">words.</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="md:col-span-4 font-body text-base text-ink-soft leading-relaxed"
          >
            Kind words from collectors, curators and clients who have spent time
            with the studio and the artist.
          </motion.p>
        </div>

        {/* Quote cards */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {testimonials.map((t, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="group relative bg-paper border border-ink-soft/15 p-8 md:p-10 flex flex-col"
              data-testid={`testimonial-${i}`}
            >
              <Quote className="h-8 w-8 text-ochre/70 mb-6" />
              <blockquote className="font-serif text-xl md:text-2xl text-ink leading-snug italic flex-1">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-8 pt-6 border-t border-ink-soft/20">
                <p className="font-body text-sm text-ink">{t.who}</p>
                <p className="font-body text-[10px] tracking-[0.25em] uppercase text-ink-soft mt-1">
                  {t.where}
                </p>
              </figcaption>
              <span className="pointer-events-none absolute inset-x-0 -bottom-px h-0.5 bg-ochre origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100" />
            </motion.figure>
          ))}
        </div>

        {/* Press strip */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="mt-16 md:mt-24 border-t border-ink-soft/25 pt-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10"
        >
          <p className="font-body text-[11px] tracking-[0.3em] uppercase text-ink-soft whitespace-nowrap">
            As featured in
          </p>
          <div className="flex flex-wrap gap-x-10 gap-y-4">
            {press.map((p, i) => (
              <span
                key={i}
                className="font-serif italic text-2xl md:text-3xl text-ink-soft/60"
              >
                {p}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
